import React from "react"
import { AnimatePresence, motion } from "motion/react"
import CheckinBodyPeekModal from "./checkin/CheckinBodyPeekModal"
import FeatureLockBadge from "./FeatureLockBadge"
import ClampedBody from "./checkin/ClampedBody"
import { formatCheckinTime, type AppliesPrecision } from "../utils/dates"

export type CheckinDiffOp = "create" | "update" | "delete"

/**
 * One tag the write changes: a feature with the values it drops and adds, or a
 * project it attaches or detaches (`project`). `locked` / `project: "locked"`
 * is a value that stays as it is but becomes Genie's.
 */
export type TagChange =
    | { key: string, title: string, removed: string[], added: string[], locked?: string[], project?: undefined }
    | { key: string, title: string, project: "added" | "removed" | "locked" }

interface CheckinDiffPanelProps {
    op: CheckinDiffOp
    currentBody?: string
    currentAppliesTo?: string
    currentAppliesAt?: string | null
    currentAppliesPrecision?: AppliesPrecision | null
    proposedBody?: string
    proposedAppliesTo?: string
    proposedAppliesAt?: string | null
    proposedAppliesPrecision?: AppliesPrecision | null
    /** Rendered as a chip row under the body; a tag-only update is just this. */
    tagChanges?: TagChange[]
    fallbackLabel?: string
    reason?: string
    /**
     * A model wrote proposedBody. The diff is the model's real output — the
     * engine generates before the panel is built, never at commit — so this is
     * provenance, not a caveat, and it's marked quietly next to the date.
     */
    generated?: boolean
    formatDate: (dateString: string, includeYearIfDifferent?: boolean) => string
    /**
     * Clip the body to three lines and offer to open it in full. Supplied by the
     * list around the panel, which owns which one write is open — so a long body
     * can't push the rest of a proposal off the screen, and two peeks can't be
     * open at once.
     */
    onPeek?: () => void
    peekOpen?: boolean
    onPeekClose?: () => void
}

export type DiffSegment = { type: "equal" | "remove" | "add", text: string }

// Tokenize into alternating word / whitespace runs. Concatenating tokens in
// order reproduces the input exactly, so we can rebuild plain spans verbatim.
const tokenize = (s: string): string[] => s.match(/\S+|\s+/g) || []

// Word-level LCS diff. Emits N remove- and M add- blocks separately rather
// than collapsing the whole changed range into one big strike-out + insert,
// so a body with two distant typo fixes shows two minimal pairs.
export const diffWords = (a: string, b: string): DiffSegment[] => {
    const aTok = tokenize(a)
    const bTok = tokenize(b)
    const n = aTok.length
    const m = bTok.length

    // dp[i][j] = LCS length of aTok[i:] and bTok[j:]. O(n*m) — fine for the
    // few-hundred-token bodies typical here.
    const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            dp[i][j] = aTok[i] === bTok[j]
                ? dp[i + 1][j + 1] + 1
                : Math.max(dp[i + 1][j], dp[i][j + 1])
        }
    }

    const segs: DiffSegment[] = []
    let i = 0
    let j = 0
    while (i < n && j < m) {
        if (aTok[i] === bTok[j]) {
            segs.push({ type: "equal", text: aTok[i] })
            i++; j++
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            segs.push({ type: "remove", text: aTok[i] })
            i++
        } else {
            segs.push({ type: "add", text: bTok[j] })
            j++
        }
    }
    while (i < n) { segs.push({ type: "remove", text: aTok[i] }); i++ }
    while (j < m) { segs.push({ type: "add", text: bTok[j] }); j++ }

    // Merge adjacent same-type segments so we emit one span per block.
    const out: DiffSegment[] = []
    for (const s of segs) {
        const last = out[out.length - 1]
        if (last && last.type === s.type) last.text += s.text
        else out.push({ ...s })
    }
    return out
}

const renderDiff = (currentText: string, proposedText: string) => {
    if (currentText === proposedText) {
        return <span>{currentText}</span>
    }
    const segs = diffWords(currentText, proposedText)
    return (
        <span>
            {segs.map((seg, i) => {
                if (seg.type === "equal") {
                    return <span key={i}>{seg.text}</span>
                }
                if (seg.type === "remove") {
                    return (
                        <span key={i} className="line-through text-rose-600 bg-rose-50 px-1">
                            {seg.text}
                        </span>
                    )
                }
                return (
                    <span key={i} className="bg-green-100 border-b-2 border-dashed border-green-500 px-1 text-green-800">
                        {seg.text}
                    </span>
                )
            })}
        </span>
    )
}

// `applies_to` reaches us as either a bare "2026-08-03" or a naive
// "2026-08-03T00:00:00", depending on which producer serialized it. Feeding the
// latter through `new Date` parses it as local midnight and `toISOString` then
// reports the previous day for any timezone east of UTC — so an edit that never
// touches the date renders under the wrong one. Both forms already start with
// the day we want, so take it literally and only parse what isn't day-prefixed.
const isoDay = (s: string): string =>
    /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : new Date(s).toISOString().split('T')[0]

type When = { appliesTo?: string, appliesAt?: string | null, precision?: AppliesPrecision | null }

// A date, and the time beside it when there is one. The time is always shown
// in the timeline's precise form, whatever the user's display setting: in a
// proposal the time can be the very thing that changes.
const whenLabel = (w: When, formatDate: (dateString: string) => string): string => {
    const day = formatDate(isoDay(w.appliesTo as string))
    const time = formatCheckinTime(w.appliesAt, 'precise', w.precision)
    return time ? `${day}, ${time}` : day
}

// Header shows when the checkin is. For updates that move it — another day, a
// new time, or no time — render the old one strike-through followed by the new
// one highlighted.
const renderDateHeader = (
    op: CheckinDiffOp,
    current: When,
    proposed: When,
    fallbackLabel: string | undefined,
    formatDate: (dateString: string) => string,
) => {
    if (op === "create" && proposed.appliesTo) {
        return <span>{whenLabel(proposed, formatDate)}</span>
    }
    if (op === "delete" && current.appliesTo) {
        return <span>{whenLabel(current, formatDate)}</span>
    }
    if (op === "update" && current.appliesTo && proposed.appliesTo) {
        const from = whenLabel(current, formatDate)
        // A write with no proposed precision predates times in proposals; it
        // kept the time, so it reads as unchanged rather than as cleared.
        const to = whenLabel(
            proposed.precision == null ? { ...current, appliesTo: proposed.appliesTo } : proposed,
            formatDate,
        )
        if (from !== to) {
            return (
                <span>
                    <span className="line-through text-rose-600 bg-rose-50 px-1">
                        {from}
                    </span>
                    <span className="bg-green-100 border-b-2 border-dashed border-green-500 px-1 ml-1 text-green-800">
                        {to}
                    </span>
                </span>
            )
        }
        return <span>{from}</span>
    }
    return <span>{fallbackLabel || ""}</span>
}

// The word diff's own two styles, so a tag change reads like a text change.
const REMOVED = "line-through text-rose-600 bg-rose-50 px-1"
const ADDED = "bg-green-100 border-b-2 border-dashed border-green-500 px-1 text-green-800"

const TagChangeChips: React.FC<{ changes: TagChange[] }> = ({ changes }) => (
    // Mounted even when empty, so the last chip of a revision still exits.
    <div className={`flex flex-wrap gap-1.5 ${changes.length ? "mb-2" : ""}`} data-testid="tag-changes">
        <AnimatePresence>
            {changes.map(change => (
                <motion.span
                    key={change.key}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.6 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    style={{ transformOrigin: 'left center' }}
                    className="inline-flex items-center gap-1 text-xs text-gray-700 bg-gray-100 rounded-full px-2 py-0.5"
                >
                    {change.project === "locked" ? (
                        <>
                            <span>{change.title}</span>
                            <FeatureLockBadge owner="assistant" className="" />
                        </>
                    ) : change.project ? (
                        <span className={change.project === "added" ? ADDED : REMOVED}>{change.title}</span>
                    ) : (
                        <>
                            <span className="font-medium">{change.title}</span>
                            {change.removed.map((v, i) => <span key={`r${i}`} className={REMOVED}>{v}</span>)}
                            {change.added.map((v, i) => <span key={`a${i}`} className={ADDED}>{v}</span>)}
                            {change.locked?.map((v, i) => <span key={`l${i}`}>{v}</span>)}
                            {change.locked && <FeatureLockBadge owner="assistant" className="" />}
                        </>
                    )}
                </motion.span>
            ))}
        </AnimatePresence>
    </div>
)

const borderColor = (op: CheckinDiffOp) =>
    op === "delete" ? "sm:border-rose-500" : "sm:border-amber-500"

const CheckinDiffPanel: React.FC<CheckinDiffPanelProps> = ({
    op,
    currentBody,
    currentAppliesTo,
    currentAppliesAt,
    currentAppliesPrecision,
    proposedBody,
    proposedAppliesTo,
    proposedAppliesAt,
    proposedAppliesPrecision,
    tagChanges = [],
    fallbackLabel,
    reason,
    generated,
    formatDate,
    onPeek,
    peekOpen = false,
    onPeekClose,
}) => {
    // The body's content and its styling are kept apart so the clamped panel and
    // the modal render the same thing at two sizes. The modal deliberately shows
    // the *diff*, not the raw proposed body — a reviewer who opens it is reading
    // the change, and a plain body would drop the very thing they clicked into.
    const bodyContent = (): React.ReactNode => {
        if (op === "create") return proposedBody || ""
        if (op === "delete") return currentBody || fallbackLabel || ""
        const oldBody = currentBody ?? ""
        const newBody = proposedBody ?? ""
        return oldBody === newBody ? newBody : renderDiff(oldBody, newBody)
    }

    const bodyClass = op === "delete"
        ? "text-gray-700 whitespace-pre-wrap line-through opacity-70"
        : "text-gray-700 whitespace-pre-wrap"
    const bodyMargin = op === "delete" ? "mb-1" : "mb-2"

    const dateHeader = renderDateHeader(
        op,
        { appliesTo: currentAppliesTo, appliesAt: currentAppliesAt, precision: currentAppliesPrecision },
        { appliesTo: proposedAppliesTo, appliesAt: proposedAppliesAt, precision: proposedAppliesPrecision },
        fallbackLabel,
        formatDate,
    )

    return (
        <div className={`sm:border-l-4 pl-3 sm:pl-4 py-3 sm:border-dashed ${borderColor(op)}`}>
            <div className="flex items-center mb-2">
                <span className="font-medium text-gray-800">
                    {dateHeader}
                </span>
                {generated && (
                    <span className="ml-2 text-xs text-gray-400 italic">generated</span>
                )}
            </div>
            {onPeek ? (
                <ClampedBody
                    className={bodyClass}
                    wrapperClassName={bodyMargin}
                    measureKey={`${op} ${currentBody ?? ""} ${proposedBody ?? ""}`}
                    onPeek={onPeek}
                >
                    {bodyContent()}
                </ClampedBody>
            ) : (
                <p className={`${bodyClass} ${bodyMargin}`}>{bodyContent()}</p>
            )}
            <TagChangeChips changes={tagChanges} />
            {reason && <div className="text-xs text-gray-500 italic mt-2">{reason}</div>}
            {onPeek && (
                <CheckinBodyPeekModal
                    open={peekOpen}
                    title={dateHeader}
                    onClose={onPeekClose ?? (() => {})}
                >
                    <p className={bodyClass}>{bodyContent()}</p>
                </CheckinBodyPeekModal>
            )}
        </div>
    )
}

export default CheckinDiffPanel
